/* eslint-disable no-use-before-define */
import {
  createStyleMixin,
  draggableMixin,
  componentNameValidationMixin,
} from '@descope-ui/common/components-mixins';
import { createBaseClass } from '@descope-ui/common/base-classes';
import { compose } from '@descope-ui/common/utils';
import { getComponentName } from '@descope-ui/common/components-helpers';
import { createImage } from './helpers';
import { injectStyle } from '@descope-ui/common/components-helpers';

export const componentName = getComponentName('image');

const srcAttrs = ['src', 'src-dark'];

class RawImage extends createBaseClass({
  componentName,
  baseSelector: 'slot',
}) {
  static get observedAttributes() {
    return srcAttrs;
  }

  constructor() {
    super();

    this.attachShadow({ mode: 'open' }).innerHTML = `
		<slot></slot>
	`;

    injectStyle(
      `
			:host {
				display: inline-flex;
			}
      :host > slot {
        width: 100%;
        height: 100%;
				box-sizing: border-box;
				display: flex;
				overflow: hidden;
			}

      ::slotted(*) {
        width: 100%;
			}

      .hidden {
        display: none;
      }
      `,
      this,
    );
  }

  init() {
    super.init?.();
    this.toggleVisibility(this.getSrc);
  }

  onThemeChange() {
    this.renderImage();
  }

  toggleVisibility(isVisible) {
    if (isVisible) {
      this.classList.remove('hidden');
    } else {
      this.classList.add('hidden');
    }
  }

  get altText() {
    return this.getAttribute('alt') || '';
  }

  get legacySrc() {
    return this.getAttribute('src');
  }

  get themeSrc() {
    return this.getAttribute(`src-${this.currentThemeName}`);
  }

  get getSrc() {
    return this.themeSrc || this.legacySrc;
  }

  // in order to fill an SVG with `currentColor` override all of its `fill` and `path` nodes
  // with the value from the `st-fill` attribute
  // eslint-disable-next-line class-methods-use-this
  updateFillColor(node) {
    // set fill to root node and all its relevant selectors
    const elementsToReplace = [node, ...node.querySelectorAll('*[fill]')];

    elementsToReplace.forEach((ele) => {
      ele.setAttribute(
        'fill',
        `var(${ImageClass.cssVarList.fill}, ${ele.getAttribute('fill') || "''"})`,
      );
    });
  }

  renderImage() {
    this.toggleVisibility(this.getSrc);

    createImage(this.getSrc, this.altText).then((res) => {
      this.innerHTML = '';
      if (res) {
        this.updateFillColor(res);
        this.appendChild(res);
      }
    });
  }

  // render only when src attribute matches current theme
  shouldRender(src) {
    const srcVal = this.getAttribute(src);
    return this.getSrc === srcVal;
  }

  attributeChangedCallback(attrName, oldValue, newValue) {
    super.attributeChangedCallback?.(attrName, oldValue, newValue);

    if (oldValue === newValue) return;

    if (this.shouldRender(attrName)) {
      this.renderImage();
    }
  }
}

export const ImageClass = compose(
  createStyleMixin({
    mappings: {
      fill: {},
      height: { selector: () => ':host' },
      width: { selector: () => ':host' },
    },
  }),
  draggableMixin,
  componentNameValidationMixin,
)(RawImage);
